import { useQuery } from '@tanstack/react-query';
import { legacy } from '@/shared/api/client';
import type {
  MgmCampaign,
  MgmCampaignList,
  MgmHealth,
  MgmLeadStats,
  MgmReferralDetail,
  MgmReferralPage,
  MgmSummary,
  ReferralStatus,
} from '../model/types';

/**
 * Painel do Indique e Ganhe: `/v1/mgm/admin/*` da legacy-api, pelo proxy
 * autenticado do BFF. `X-Passbook-Id` faz o BFF barrar loja fora da sessão;
 * a API confere de novo (master, ou flag + passbook do usuário).
 */

interface Envelope<T> {
  data: T;
}

const base = (idPassbook: string) => `/mgm/admin/${idPassbook}`;
const scoped = (idPassbook: string) => ({ headers: { 'X-Passbook-Id': idPassbook } });

export const mgmKeys = {
  all: (idPassbook: string) => ['mgm', idPassbook] as const,
  campaigns: (idPassbook: string) => ['mgm', idPassbook, 'campaigns'] as const,
  summary: (idPassbook: string, campaignId: string) => ['mgm', idPassbook, 'summary', campaignId] as const,
  referrals: (idPassbook: string, qs: string) => ['mgm', idPassbook, 'referrals', qs] as const,
  referral: (idPassbook: string, id: string) => ['mgm', idPassbook, 'referral', id] as const,
  lead: (idPassbook: string, userId: string) => ['mgm', idPassbook, 'lead', userId] as const,
  health: (idPassbook: string) => ['mgm', idPassbook, 'health'] as const,
};

export function useMgmCampaigns(idPassbook: string) {
  return useQuery({
    queryKey: mgmKeys.campaigns(idPassbook),
    queryFn: async () => (await legacy.get<Envelope<MgmCampaignList>>(`${base(idPassbook)}/campaigns`, scoped(idPassbook))).data,
    enabled: Boolean(idPassbook),
    staleTime: 30 * 1000,
  });
}

export async function saveMgmCampaign(idPassbook: string, id: string | null, body: unknown): Promise<MgmCampaign> {
  const res = id
    ? await legacy.put<Envelope<MgmCampaign>>(`${base(idPassbook)}/campaigns/${id}`, body, scoped(idPassbook))
    : await legacy.post<Envelope<MgmCampaign>>(`${base(idPassbook)}/campaigns`, body, scoped(idPassbook));
  return res.data;
}

export async function setMgmCampaignActive(idPassbook: string, id: string, active: boolean): Promise<MgmCampaign> {
  const res = await legacy.put<Envelope<MgmCampaign>>(
    `${base(idPassbook)}/campaigns/${id}/status`,
    { active },
    scoped(idPassbook),
  );
  return res.data;
}

/** Relatório de UMA campanha — só dispara quando a campanha já é conhecida. */
export function useMgmSummary(idPassbook: string, campaignId: string) {
  return useQuery({
    queryKey: mgmKeys.summary(idPassbook, campaignId),
    queryFn: async () =>
      (
        await legacy.get<Envelope<MgmSummary>>(
          `${base(idPassbook)}/summary?campaignId=${encodeURIComponent(campaignId)}`,
          scoped(idPassbook),
        )
      ).data,
    enabled: Boolean(idPassbook && campaignId),
    staleTime: 30 * 1000,
  });
}

/** Tamanho do código do link de indicação (helpers/mgmCode.js no fixe-api). */
export const REFERRAL_CODE_LENGTH = 8;

export interface ReferralFilters {
  campaignId?: string;
  status?: ReferralStatus | '';
  code?: string;
  page: number;
  limit: number;
}

export function referralQueryString(filters: ReferralFilters): string {
  const params = new URLSearchParams();
  if (filters.campaignId) params.set('campaignId', filters.campaignId);
  if (filters.status) params.set('status', filters.status);
  // Código parcial não filtra nada no backend: só manda quando estiver completo.
  const code = filters.code?.trim() ?? '';
  if (code.length === REFERRAL_CODE_LENGTH) params.set('code', code);
  params.set('page', String(filters.page));
  params.set('limit', String(filters.limit));
  return params.toString();
}

export function useMgmReferrals(idPassbook: string, filters: ReferralFilters, enabled = true) {
  const qs = referralQueryString(filters);
  return useQuery({
    queryKey: mgmKeys.referrals(idPassbook, qs),
    queryFn: async () =>
      (await legacy.get<Envelope<MgmReferralPage>>(`${base(idPassbook)}/referrals?${qs}`, scoped(idPassbook))).data,
    enabled: Boolean(idPassbook) && enabled,
    placeholderData: (prev) => prev,
  });
}

export function useMgmReferral(idPassbook: string, id: string | null) {
  return useQuery({
    queryKey: mgmKeys.referral(idPassbook, id ?? ''),
    queryFn: async () =>
      (await legacy.get<Envelope<MgmReferralDetail>>(`${base(idPassbook)}/referrals/${id}`, scoped(idPassbook))).data,
    enabled: Boolean(idPassbook && id),
  });
}

export function useMgmLead(idPassbook: string, userId: string, enabled = true) {
  return useQuery({
    queryKey: mgmKeys.lead(idPassbook, userId),
    queryFn: async () =>
      (await legacy.get<Envelope<MgmLeadStats>>(`${base(idPassbook)}/leads/${userId}`, scoped(idPassbook))).data,
    enabled: Boolean(idPassbook && userId && enabled),
  });
}

export function useMgmHealth(idPassbook: string, enabled: boolean) {
  return useQuery({
    queryKey: mgmKeys.health(idPassbook),
    queryFn: async () => (await legacy.get<Envelope<MgmHealth>>(`${base(idPassbook)}/health`, scoped(idPassbook))).data,
    enabled: Boolean(idPassbook && enabled),
    // Falha de lançamento é silenciosa para a loja: a tela aberta percebe sozinha.
    refetchInterval: 60 * 1000,
  });
}

export async function retryMgmReward(idPassbook: string, rewardId: string): Promise<void> {
  await legacy.post(`${base(idPassbook)}/rewards/${rewardId}/retry`, {}, scoped(idPassbook));
}
